// Edit one audience rule (program default or session override) in a dialog:
// name, ConditionGroupBuilder, assignment type, dynamic/fixed, Preview, Save.
// Saving PUTs through the caller's `onSave` (program or session) and surfaces
// the server's own 400 message on an invalid rule; the caller toasts the
// `resolved` summary. Removing an override is `onSave({ rule: null })`.
import { useT } from '../lib/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { ConditionGroupBuilder } from '@/features/shared/components/ConditionGroupBuilder';
import { validateRule, type RuleNode } from '@/features/shared/components/conditionGroup';
import { getApiErrorMessage } from '@/lib/apiError';
import type { AssignmentType, AudienceMode, AudienceRule, AudienceRuleInput, AudienceRuleResult } from '@/services/learningService';
import { useAudienceFacets, useAudiencePreview } from '../api/hooks';
import { useRuleVocabulary } from '../lib/audienceRules';
import { AudiencePreviewPanel } from './AudiencePreviewPanel';

const selectCls = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Existing rule (null = none yet). */
  initial: AudienceRule | null;
  /** Suggested name when there is no rule yet (e.g. the program name). */
  defaultName?: string;
  /** Explicit people (session overrides) so Preview reflects the roster the same way publish will. */
  userIds?: string[];
  onSave: (body: AudienceRuleInput) => Promise<AudienceRuleResult>;
  onSaved?: (r: AudienceRuleResult) => void;
  /** Show a "Remove rule" action (sends rule: null). */
  allowRemove?: boolean;
  removeLabel?: string;
}

export const AudienceRuleDialog: React.FC<Props> = ({ open, onClose, title, description, initial, defaultName, userIds, onSave, onSaved, allowRemove, removeLabel }) => {
  const t = useT();
  const facetsQ = useAudienceFacets(open);
  const vocab = useRuleVocabulary(t, facetsQ.data);
  const preview = useAudiencePreview();
  const [name, setName] = useState('');
  const [rule, setRule] = useState<RuleNode | null>(null);
  const [assignmentType, setAssignmentType] = useState<AssignmentType | ''>('');
  const [mode, setMode] = useState<AudienceMode | ''>('');
  const [busy, setBusy] = useState<'save' | 'remove' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || defaultName || '');
    setRule(initial?.rule || null);
    setAssignmentType((initial?.assignment_type as AssignmentType) || '');
    setMode((initial?.mode as AudienceMode) || '');
    setErr(null); setBusy(null); preview.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id, initial?.updated_at]);

  const issues = useMemo(() => validateRule(rule), [rule]);
  const canSave = !!rule && issues.length === 0 && !busy;

  const save = async (body: AudienceRuleInput, kind: 'save' | 'remove') => {
    setErr(null); setBusy(kind);
    try {
      const r = await onSave(body);
      onSaved?.(r);
      onClose();
    } catch (e) {
      setErr(getApiErrorMessage(e, t('training:err', 'Something went wrong')));
    } finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : <DialogDescription>{t('training:rule_dialog_help', 'People are matched by the platform when you save and whenever the roster changes (dynamic). Use Preview to see who qualifies today.')}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="space-y-1 sm:col-span-3">
              <Label htmlFor="ard-name" className="text-xs">{t('training:rule_name', 'Rule name')}</Label>
              <Input id="ard-name" className="h-9" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('training:rule_name_ph', 'e.g. Finance + HR')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ard-atype" className="text-xs">{t('training:assignment_type', 'Assignment type')}</Label>
              <select id="ard-atype" className={selectCls} value={assignmentType} onChange={(e) => setAssignmentType(e.target.value as AssignmentType | '')}>
                <option value="">{t('training:wiz_program_default', 'Program default')}</option>
                <option value="required">{t('training:required', 'Required')}</option>
                <option value="recommended">{t('training:recommended', 'Recommended')}</option>
                <option value="optional">{t('training:optional', 'Optional')}</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ard-mode" className="text-xs">{t('training:wiz_audience_mode', 'Audience mode')}</Label>
              <select id="ard-mode" className={selectCls} value={mode} onChange={(e) => setMode(e.target.value as AudienceMode | '')}>
                <option value="">{t('training:wiz_program_default', 'Program default')}</option>
                <option value="dynamic">{t('training:audience_dynamic_short', 'Dynamic')}</option>
                <option value="fixed">{t('training:audience_fixed_short', 'Fixed')}</option>
              </select>
            </div>
          </div>
          {facetsQ.isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('training:loading', 'Loading')}…</div>}
          {facetsQ.isError && <p className="text-sm text-red-600 dark:text-red-400">{getApiErrorMessage(facetsQ.error, t('training:facets_unavailable', 'The rule builder vocabulary could not be loaded.'))}</p>}
          {facetsQ.data && (
            <ConditionGroupBuilder idPrefix="ard" facets={vocab.facets} options={vocab.options} people={vocab.people} labels={vocab.labels} value={rule} onChange={setRule} disabled={!!busy} />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" disabled={!rule || issues.length > 0 || preview.isPending} onClick={() => preview.mutate({ rule, user_ids: userIds && userIds.length ? userIds : undefined })}>
              {preview.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:wiz_preview', 'Preview matched employees')}
            </Button>
          </div>
          {preview.data && <AudiencePreviewPanel preview={preview.data} className="rounded-md border border-border p-2" />}
          {err && <div role="alert" className="rounded-md border border-red-300 bg-red-50 dark:bg-red-500/10 p-2 text-sm text-red-700 dark:text-red-300">{err}</div>}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border">
            <div>
              {allowRemove && initial && (
                <Button size="sm" variant="ghost" className="text-red-600" disabled={!!busy} onClick={() => save({ name: initial.name, rule: null }, 'remove')}>
                  {busy === 'remove' ? <Loader2 className="h-4 w-4 animate-spin" /> : (removeLabel || t('training:rule_remove', 'Remove rule'))}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" disabled={!!busy} onClick={onClose}>{t('training:cancel', 'Cancel')}</Button>
              <Button size="sm" disabled={!canSave} onClick={() => save({ name: name.trim() || defaultName || t('training:rule_default_name', 'Audience rule'), rule, assignment_type: assignmentType || undefined, mode: mode || undefined }, 'save')}>
                {busy === 'save' ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:save', 'Save')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AudienceRuleDialog;
